import styles from './FormNoteControls.module.css';
import formStyles from '@he-novation/design-system/styles/form-styles.module.css';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
    AreaSelectionShape,
    AreaSelectionType
} from '@he-novation/config/types/area-selection.types';
import { FileEnum } from '@he-novation/config/types/file.types';
import { AbsoluteContentButton } from '@he-novation/design-system/components/buttons/AbsoluteContentButton/AbsoluteContentButton';
import { Button } from '@he-novation/design-system/components/buttons/Button/Button';
import { FormField } from '@he-novation/design-system/components/form/FormField/FormField';
import { Icon } from '@he-novation/design-system/components/graphics/Icon/Icon';
import { colorBgDark } from '@he-novation/design-system/constants/constants.style';
import { DirectionX, DirectionY } from '@he-novation/design-system/utils/getAbsolutePosition';
import { timeCodeToSeconds } from '@he-novation/lib-timecodes';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import LocalStorageWrapper from '../../../../../helpers/LocalStorageWrapper';

import { areaSelectionAtom } from '$atoms/file-atoms/area-selection-atom';
import { audioAtom } from '$atoms/file-atoms/audio-atom';
import { videoAtom } from '$atoms/file-atoms/video-atom';
import { useAreaSelection } from '$hooks/useAreaSelection';
import { useAudioControls } from '$hooks/useAudioControls';
import { useTranslate } from '$hooks/useTranslate';
import { useVideoControls } from '$hooks/useVideoControls';
import { pageAndPageNumberSelector } from '$redux/content/file/fileSelectors';

type FormNoteControlsProps = {
    formId: string;
    fileType: FileEnum;
};

function shouldFixTimecodes(tcIn: string, tcOut: string) {
    const tcInSeconds = timeCodeToSeconds(tcIn, 25);
    const tcOutSeconds = timeCodeToSeconds(tcOut, 25);
    return tcInSeconds > tcOutSeconds;
}

function hasAreaSelectionNotes(fileType: FileEnum) {
    return ![FileEnum.Document, FileEnum.Office, FileEnum.Markdown].includes(fileType);
}

export function FormNoteControls({ formId, fileType }: FormNoteControlsProps) {
    const { t } = useTranslate();

    const [type, setType] = useState(hasAreaSelectionNotes(fileType) ? 'areaselection' : 'global');
    const [selectionType, setSelectionType] = useState<AreaSelectionType>(
        AreaSelectionType.Multiple
    );
    const [shapeType, setShapeType] = useState<AreaSelectionShape>(AreaSelectionShape.Freehand);
    const videoState = useAtomValue(videoAtom);
    const audioState = useAtomValue(audioAtom);
    const videoControls = useVideoControls();
    const audioControls = useAudioControls();
    const { toggleAreaSelection, resetShapes, startShape, setColor, spliceShape } =
        useAreaSelection();
    const { page } = useSelector(pageAndPageNumberSelector);
    const areaSelection = useAtomValue(areaSelectionAtom);

    const currentTimecode =
        fileType === 'video' ? videoState.timecodeWithTimecodeStart : audioState.timecode;

    const [tcIn, setTcIn] = useState(currentTimecode);
    const [tcOut, setTcOut] = useState(currentTimecode);

    const audioTimecode = fileType === 'audio';

    const colors = ['#ff0000', '#00ff00', '#ffff00', '#00ffff'];

    useEffect(() => {
        setColor(LocalStorageWrapper.getItem('areaSelectionColor') || colors[0]);
        return () => {
            toggleAreaSelection(false);
            resetShapes();
        };
    }, []);

    useEffect(() => {
        resetShapes();
        if (type !== 'areaselection') {
            toggleAreaSelection(false);
            return;
        }
        if (fileType === 'video') videoControls.pause();
        if (fileType === 'audio') audioControls.pause();
        startShape(
            selectionType,
            selectionType === AreaSelectionType.Single ? AreaSelectionShape.Rectangle : shapeType
        );
    }, [type, selectionType]);

    useEffect(() => {
        if (type !== 'sequence') {
            setTcIn(currentTimecode);
            setTcOut(currentTimecode);
        }
    }, [currentTimecode, type]);

    useEffect(() => {
        startShape(AreaSelectionType.Multiple, shapeType);
    }, [shapeType]);

    const setTimecode = useCallback((truth: 'in' | 'out', tcIn: string, tcOut: string) => {
        const [setValue, setFixes] = truth === 'in' ? [setTcIn, setTcOut] : [setTcOut, setTcIn];
        const v = truth === 'in' ? tcIn : tcOut;
        if (shouldFixTimecodes(tcIn, tcOut)) {
            setFixes(v);
        }
        setValue(v);
    }, []);

    let shapeIcon = 'brush';
    switch (shapeType) {
        case AreaSelectionShape.Rectangle:
            shapeIcon = 'square';
            break;
        case AreaSelectionShape.Circle:
            shapeIcon = 'circle';
            break;
        case AreaSelectionShape.Freehand:
            shapeIcon = 'brush';
            break;
        default:
            shapeIcon = 'brush';
            break;
    }

    return (
        <div className={styles.controls}>
            <FormField
                formId={formId}
                name="type"
                className={formStyles.dark}
                type={'select'}
                value={type}
                onChange={(e) => setType(e.currentTarget.value)}
            >
                <option value="global">{t('player.Global')}</option>
                {hasAreaSelectionNotes(fileType) && (
                    <option value="areaselection">{t('player.Zone')}</option>
                )}
                {(fileType === 'video' || fileType === 'audio') && (
                    <option value="sequence">{t('player.Sequence')}</option>
                )}
            </FormField>

            {type === 'global' && fileType === FileEnum.PDF && page !== undefined && (
                <span className={styles.page}>
                    {t('common.Page')} {page + 1}
                </span>
            )}

            {type === 'areaselection' && (
                <>
                    {['audio', 'video'].includes(fileType) && (
                        <FormField
                            className={styles.timecode}
                            formId={formId}
                            name="tcIn"
                            audio={audioTimecode}
                            type={'timecode'}
                            value={tcIn}
                        />
                    )}
                    <div className={styles.selectionSettings}>
                        <AbsoluteContentButton
                            direction={[DirectionX.RightInner, DirectionY.Bottom]}
                            triggersOnHover={false}
                            className="!h-full !p-8"
                            content={
                                <div
                                    className={classNames(
                                        styles.brushActions,
                                        'flex items-center justify-center gap-20 first:rounded-l-lg last:rounded-r-lg'
                                    )}
                                >
                                    <div className="flex items-center justify-center ">
                                        <Button
                                            onClick={() => {
                                                const lastShapeIndex =
                                                    areaSelection.selection.length - 1;
                                                if (lastShapeIndex >= 0) {
                                                    spliceShape(lastShapeIndex);
                                                }
                                            }}
                                            icon={{
                                                name: 'arrow_reply',
                                                stroke: 'white',
                                                fill: 'none',
                                                style:
                                                    areaSelection.selection.length === 0
                                                        ? {
                                                              opacity: 0.5
                                                          }
                                                        : {}
                                            }}
                                            className={classNames(
                                                'flex',
                                                'justify-center',
                                                styles.invert
                                            )}
                                            disabled={areaSelection.selection.length === 0}
                                        />
                                    </div>
                                    <div className="flex items-center justify-center ">
                                        <Button
                                            icon={{
                                                name: 'brush',
                                                stroke: 'white',
                                                fill: 'none'
                                            }}
                                            onClick={() => {
                                                setSelectionType(AreaSelectionType.Multiple);
                                                setShapeType(AreaSelectionShape.Freehand);
                                            }}
                                            className={classNames(
                                                'flex',
                                                'justify-center',
                                                selectionType === AreaSelectionType.Multiple &&
                                                    shapeType === AreaSelectionShape.Freehand
                                                    ? styles.shapeSelected
                                                    : styles.shapeNotSelected
                                            )}
                                        />
                                        <Button
                                            icon={{
                                                name: 'square',
                                                stroke: 'white',
                                                fill: 'none'
                                            }}
                                            onClick={() => {
                                                setSelectionType(AreaSelectionType.Multiple);
                                                setShapeType(AreaSelectionShape.Rectangle);
                                            }}
                                            className={classNames(
                                                'flex',
                                                'justify-center',
                                                selectionType === AreaSelectionType.Multiple &&
                                                    shapeType === AreaSelectionShape.Rectangle
                                                    ? styles.shapeSelected
                                                    : styles.shapeNotSelected
                                            )}
                                        />
                                        <Button
                                            icon={{
                                                name: 'circle',
                                                stroke: 'white',
                                                fill: 'none',
                                                className: styles.shapeNotSelected
                                            }}
                                            onClick={() => {
                                                setSelectionType(AreaSelectionType.Multiple);
                                                setShapeType(AreaSelectionShape.Circle);
                                            }}
                                            className={classNames(
                                                'flex',
                                                'justify-center',
                                                selectionType === AreaSelectionType.Multiple &&
                                                    shapeType === AreaSelectionShape.Circle
                                                    ? styles.shapeSelected
                                                    : styles.shapeNotSelected
                                            )}
                                        />
                                    </div>
                                    {/* <div className="flex items-center justify-center ">
                                        <Button
                                            icon={{
                                                name: 'square',
                                                stroke:
                                                    selectionType === AreaSelectionType.Single
                                                        ? colorBgLightest
                                                        : colorText,
                                                fill: 'none'
                                            }}
                                            onClick={useCallback(() => {
                                                setSelectionType(AreaSelectionType.Single);
                                            }, [])}
                                            className="flex justify-center"
                                        />
                                    </div> */}

                                    <div className="box-content flex items-center justify-center gap-8">
                                        {colors.map((color) => (
                                            <div
                                                key={color}
                                                className={classNames(styles.colorBox, {
                                                    [styles.selected]: color === areaSelection.color
                                                })}
                                                onClick={() => {
                                                    setColor(color);
                                                }}
                                            >
                                                <div
                                                    className={styles.colorBoxInner}
                                                    style={{ backgroundColor: color }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            }
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                            }}
                        >
                            <Button
                                icon={{
                                    name: shapeIcon,
                                    stroke: 'black',
                                    fill: 'none'
                                }}
                                className="m-auto flex h-full items-center justify-center rounded-2xl !py-8"
                                style={{
                                    backgroundColor:
                                        selectionType === AreaSelectionType.Multiple
                                            ? areaSelection.color
                                            : colorBgDark
                                }}
                            />
                        </AbsoluteContentButton>
                    </div>
                </>
            )}

            {type === 'sequence' && (
                <div className={styles.timecode}>
                    <Button
                        icon={[{ name: 'pin', stroke: 'none', fill: 'white' }]}
                        type="button"
                        onClick={() => setTimecode('in', currentTimecode, tcOut)}
                    />
                    <FormField
                        formId={formId}
                        name="tcIn"
                        type={'timecode'}
                        audio={audioTimecode}
                        value={tcIn}
                        onBlur={(e) => {
                            setTimecode('in', (e.currentTarget as HTMLInputElement).value, tcIn);
                        }}
                    />
                    <Icon
                        className={styles.chevron}
                        icon={[{ name: 'chevron', fill: 'none', stroke: 'white' }]}
                    />
                    <FormField
                        formId={formId}
                        name="tcOut"
                        type={'timecode'}
                        audio={audioTimecode}
                        value={tcOut}
                        onBlur={(e) => {
                            setTimecode('out', tcIn, (e.currentTarget as HTMLInputElement).value);
                        }}
                    />
                    <Button
                        icon={[{ name: 'pin', stroke: 'none', fill: 'white' }]}
                        type="button"
                        onClick={() => setTimecode('out', tcIn, currentTimecode)}
                    />
                </div>
            )}
        </div>
    );
}
